<template>
  <div class="ai-report-detail-container pb-3">
    <!-- 加载状态 -->
    <div v-if="loading" class="text-center py-5 my-5">
      <b-spinner small variant="primary"></b-spinner>
    </div>

    <!-- 极致紧凑详情卡片 -->
    <div v-else-if="report" class="detail-card shadow-sm rounded-lg overflow-hidden border" :class="{ 'chart-blurred': requireSubscription }">
      <!-- 紧凑头部 -->
      <div class="detail-header px-3 py-1 text-white">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center overflow-hidden flex-grow-1">
            <b-icon icon="arrow-left" class="cursor-pointer mr-2 back-icon" @click="$router.go(-1)"></b-icon>
            <div class="d-flex flex-column flex-md-row align-items-md-center overflow-hidden">
              <span class="font-weight-bold mr-md-2 text-truncate report-title-text">AI每日盘前【{{ report.created_time ? report.created_time.split(' ')[0] : '' }}】</span>
              <div class="d-flex align-items-center opacity-8 x-small mt-0 info-bar flex-wrap">
                <span class="mr-2 d-none d-sm-inline"><b-icon icon="person" class="mr-1"></b-icon>{{ report.username || 'AI' }}</span>
                <span class="mr-2"><b-icon icon="clock" class="mr-1"></b-icon>{{ report.created_time }}</span>
                <span class="cursor-pointer ml-1 like-indicator" @click="toggleLike">
                  <img :src="isLiked ? likedIcon : likeIcon" class="mr-1 thumb-icon-small" />
                  <span class="likes-count">{{ report.likes || 0 }}</span>
                </span>
              </div>
            </div>
          </div>
          <div class="ml-2 flex-shrink-0">
            <b-icon icon="share" class="cursor-pointer share-icon" @click="shareReport"></b-icon>
          </div>
        </div>
      </div>

      <!-- 紧凑正文 -->
      <div class="detail-body px-3 py-2">
        <article class="report-content" v-html="formattedContent" @click="handleContentClick"></article>

        <!-- 紧凑图片 -->
        <div v-if="images.length" class="report-images mt-2 pt-1 border-top">
          <div v-for="(img, idx) in images" :key="idx" class="mb-1 text-center">
            <img :src="img" class="img-fluid rounded border" style="max-height: 600px;" />
          </div>
        </div>
        
        <!-- 文章末尾声明 -->
        <div class="mt-3 py-2 border-top text-center">
          <span class="text-muted" style="font-size: 0.8rem; opacity: 0.8;">声明：AI生成内容, 仅作为学习交流之用, 请勿用于投资。</span>
        </div>
        
        <!-- 底部交互 - 极致紧凑 -->
        <div class="mt-2 pt-1 border-top d-flex justify-content-center">
          <b-button :variant="isLiked ? 'primary' : 'outline-primary'" size="sm" class="rounded-pill px-3 py-0" style="height: 20px; font-size: 0.7rem;" @click="toggleLike">
            <b-icon :icon="isLiked ? 'hand-thumbs-up-fill' : 'hand-thumbs-up'" class="mr-1" font-scale="0.8"></b-icon>
            {{ isLiked ? '已赞' : '有用' }}
          </b-button>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-5">
      <p class="text-muted">未找到报告</p>
    </div>

    <!-- Subscription Overlay -->
    <div v-if="requireSubscription" class="subscription-overlay">
      <div class="overlay-content">
        <b-icon icon="lock" font-scale="3" class="mb-3 lock-icon"></b-icon>
        <h3>该功能属于高级付费模块</h3>
        <p>解锁【AI每日盘前】模块，获取每日早盘核心主线、龙头个股及详细催化逻辑。</p>
        <b-button variant="primary" size="lg" @click="$router.push({ path: '/subscription', query: { module: 'ai_premarket' }})">前往解锁</b-button>
      </div>
    </div>

    <!-- 股票K线侧边栏 -->
    <StockInfoSidebar :symbol_code="sidebarSymbolCode" :symbol_name="sidebarSymbolName" :date_mark="sidebarDateMark" />

    <!-- 分享弹窗 -->
    <b-modal v-model="showShareModal" title="分享报告" centered hide-footer @shown="generateShareQr">
      <div class="text-center p-3">
        <p class="text-muted small mb-1">邀请好友注册，您将获得分享积分奖励（{{ rewardPoints }}积分）</p>
        <p v-if="!hasInviteCode" class="text-danger x-small mb-3">（注意：您当前未登录，分享后可能无法获得积分奖励）</p>
        
        <div class="qr-container bg-white p-3 rounded border mx-auto mb-3" style="width: 200px; height: 200px;">
          <canvas id="share-qr-canvas" style="width: 100%; height: 100%;"></canvas>
        </div>
        
        <div class="share-link-box p-2 bg-light rounded border text-left mb-3">
          <div class="small text-muted mb-1">分享链接:</div>
          <div class="text-truncate text-primary x-small">{{ shareUrl }}</div>
        </div>
        
        <b-button variant="primary" block @click="copyShareUrl">复制链接</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'
import QRCode from 'qrcode'

export default {
  name: 'WonderAiReportDetail',
  components: {
    StockInfoSidebar
  },
  data() {
    return {
      reportId: this.$route.params.id,
      report: null,
      loading: true,
      isLiked: false,
      likeIcon: require('assets/img/like.png'),
      likedIcon: require('assets/img/liked.png'),
      requireSubscription: false,
      // 侧边栏相关
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      sidebarDateMark: '',
      // 分享相关
      showShareModal: false,
      shareUrl: '',
      hasInviteCode: false,
      rewardPoints: 10
    }
  },
  computed: {
    formattedContent() {
      if (!this.report?.content) return ''
      let c = this.report.content
        // 1. 预处理：移除板块标题前的多余换行，确保布局紧凑
        .replace(/\n+(?=概念：|核心逻辑：|催化事件：|核心股票:|其他股票:)/g, '\n')
        .replace(/\n/g, '<br>')
        .replace(/概念：(<br>)?/g, '<span class="section-badge theme-badge">题材 <span class="badge-arrow">→</span></span><strong class="text-primary">')
        .replace(/核心逻辑：(<br>)?/g, '</strong><span class="section-badge logic-badge">逻辑 <span class="badge-arrow">→</span></span>')
        .replace(/催化事件：(<br>)?/g, '<span class="section-badge catalyst-badge">催化 <span class="badge-arrow">→</span></span>')
        .replace(/核心股票:\s*(<br>)?/g, '<span class="section-badge stock-badge">核心标的:</span>')
        .replace(/其他股票:\s*(<br>)?/g, '<span class="section-badge other-badge">其他标的:</span>')
        .replace(/\[(.*?)\]/g, (match, content) => {
          // 提取名称和代码，例如 [中际旭创(300308) +1.17%]
          const stockMatch = content.match(/^(.*?)\((\d{6})\)/)
          let dataAttrs = ''
          if (stockMatch) {
            const name = stockMatch[1]
            const code = stockMatch[2]
            dataAttrs = `data-code="${code}" data-name="${name}"`
          }
          const isUp = content.includes('+')
          const colorClass = isUp ? 'text-danger' : (content.includes('-') ? 'text-success' : 'text-primary')
          return `<span class="stock-tag ${colorClass} ${dataAttrs ? 'cursor-pointer' : ''}" ${dataAttrs}>${content}</span>`
        })
        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-dark">$1</strong>')
      
      return c
    },
    images() {
      if (!this.report?.images) return []
      try {
        const imgs = typeof this.report.images === 'string' 
          ? JSON.parse(this.report.images) 
          : this.report.images
        return Array.isArray(imgs) ? imgs : [imgs]
      } catch (e) {
        return []
      }
    }
  },
  mounted() {
    this.loadDetail()
    this.fetchRewardConfig()
  },
  methods: {
    fetchRewardConfig() {
      request_zzquant({
        url: '/v3/open/referral/config',
        method: 'GET'
      })
      .then(res => {
        if (res?.data?.code === 20000) {
          this.rewardPoints = res.data.data.reward_points || 10
        }
      })
      .catch(err => {
        console.warn('Fetch reward config error:', err)
      })
    },
    loadDetail() {
      this.loading = true
      request_zzquant({
        url: `/v3/ai-report/detail/${this.reportId}`,
        method: 'GET'
      })
      .then(res => {
        this.report = res?.data?.data || null
        if (this.report) {
          this.isLiked = !!this.report.liked_by_me
          // 更新浏览器标题
          const reportDate = this.report.created_time ? this.report.created_time.split(' ')[0] : ''
          if (reportDate) {
            document.title = `AI每日盘前【${reportDate}】`
          }
        }
      })
      .catch(err => {
        const status = err.status || (err.response && err.response.status)
        if (status === 403) {
          this.requireSubscription = true
          // Mock some report data for blurred preview
          this.report = {
            title: '【AI盘前】今日市场主线前瞻与核心标的',
            username: 'AI',
            created_time: new Date().toISOString().split('T')[0] + ' 08:30:00',
            content: '核心逻辑：今日市场预计将围绕[某主线]展开...<br>催化事件：某重磅会议召开...<br>核心股票: [标的A(000001)] [标的B(000002)]<br>概念：数字经济<br>这是预览内容，实际内容包含非常详细的分析...',
            likes: 102
          }
        } else {
          console.error('Load report detail error:', err)
        }
      })
      .finally(() => {
        this.loading = false
      })
    },
    handleContentClick(e) {
      // 通过事件委托处理 v-html 中的点击
      const target = e.target.closest('.stock-tag')
      if (target && target.dataset.code) {
        this.openStockSidebar(target.dataset.code, target.dataset.name)
      }
    },
    openStockSidebar(code, name) {
      if (!code) return
      this.sidebarSymbolCode = code
      this.sidebarSymbolName = name || ''
      // 传递报告日期作为标记，sidebar 内部会自动格式化并标注 K 线位置
      this.sidebarDateMark = this.report ? this.report.created_time : ''
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
    },
    toggleLike() {
      if (!this.report) return
      
      request_zzquant({
        url: '/v3/ai-report/like',
        method: 'POST',
        params: {
          post_id: this.reportId
        }
      })
      .then(res => {
        const d = res?.data?.data || {}
        if (res?.data?.code === 20000) {
          this.isLiked = !!d.liked
          this.report.likes = d.likes ?? this.report.likes
          
          if (this.isLiked) {
            this.$bvToast.toast('点赞成功', { variant: 'success', solid: true, appendToast: true, toaster: 'b-toaster-top-center', autoHideDelay: 1500 })
          }
        } else {
          this.$bvToast.toast(res?.data?.message || '操作失败', { variant: 'warning', solid: true, appendToast: true, toaster: 'b-toaster-top-center' })
        }
      })
      .catch(err => {
        console.error('Toggle like error:', err)
        const msg = err.response?.data?.detail || '请先登录'
        this.$bvToast.toast(msg, { variant: 'danger', solid: true, appendToast: true, toaster: 'b-toaster-top-center' })
      })
    },
    shareReport() {
      let url = window.location.href
      let inviteCode = localStorage.getItem('my_invite_code')
      
      // 兜底：尝试从 user_info 获取
      if (!inviteCode) {
        try {
          const info = JSON.parse(localStorage.getItem('user_info'))
          if (info && info.invite_code) {
            inviteCode = info.invite_code
          }
        } catch (e) { /* ignore */ }
      }

      if (inviteCode) {
        this.hasInviteCode = true
        // 只有当 URL 里没有 invite_code 时才添加，或者直接替换为自己的？
        // 按照“不清理”的要求，我们只在没有时添加，或者保持现状
        if (!url.includes('invite_code=')) {
          if (url.includes('?')) {
            url += `&invite_code=${inviteCode}`
          } else {
            url += `?invite_code=${inviteCode}`
          }
        }
      } else {
        this.hasInviteCode = false
      }
      
      this.shareUrl = url
      this.showShareModal = true
    },
    generateShareQr() {
      const canvas = document.getElementById('share-qr-canvas')
      if (!canvas || !this.shareUrl) return
      
      QRCode.toCanvas(canvas, this.shareUrl, {
        width: 168,
        margin: 1,
        color: {
          dark: '#333',
          light: '#fff'
        }
      }, (error) => {
        if (error) console.error('Generate QR error:', error)
      })
    },
    copyShareUrl() {
      navigator.clipboard.writeText(this.shareUrl).then(() => {
        this.$bvToast.toast('分享链接已复制', { variant: 'success', solid: true, appendToast: true, toaster: 'b-toaster-top-center', autoHideDelay: 1500 })
        this.showShareModal = false
      }).catch(() => {
        this.$bvToast.toast('复制失败，请手动复制', { variant: 'danger', solid: true })
      })
    }
  }
}
</script>

<style scoped>
.ai-report-detail-container {
  max-width: 900px;
  margin: 0 auto;
}

.detail-card {
  border-color: #eee !important;
}

.detail-header {
  background: linear-gradient(135deg, #007bff 0%, #00c6ff 100%);
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s;
}
.cursor-pointer:hover {
  opacity: 0.7;
}

.report-title-text {
  font-size: 1rem;
  max-width: 100%;
}

.x-small {
  font-size: 0.8rem;
  font-weight: 500;
}

.opacity-8 {
  opacity: 0.9;
}

.like-indicator {
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
  padding: 0 4px;
  border-radius: 4px;
}
.like-indicator:hover {
  background: rgba(255, 255, 255, 0.2);
}
.thumb-icon-small {
  width: 14px;
  height: 14px;
  object-fit: contain;
  filter: brightness(1.5); /* 增加亮度以适应深色背景 */
}
.likes-count {
  font-weight: 600;
  margin-left: 1px;
}

.report-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
}

/* 内容板块样式 */
.report-content >>> .section-badge {
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  height: 22px;
  line-height: 22px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 800;
  margin-right: 10px;
  vertical-align: middle;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.report-content >>> .badge-arrow {
  margin-left: 4px;
  font-family: serif;
  font-weight: bold;
}

.report-content >>> .theme-badge { background-color: #007bff; color: #fff; }
.report-content >>> .logic-badge { background-color: #20bf6b; color: #fff; }
.report-content >>> .catalyst-badge { background-color: #eb3b5a; color: #fff; }
.report-content >>> .stock-badge { background-color: #4b6584; color: #fff; }
.report-content >>> .other-badge { background-color: #778ca3; color: #fff; }

.report-content >>> .stock-tag {
  display: inline-block;
  padding: 0 4px;
  margin: 1px 2px;
  background-color: #f8f9fa;
  border-radius: 3px;
  font-weight: 600;
  font-size: 0.9rem;
}

.report-content >>> .cursor-pointer {
  cursor: pointer;
  text-decoration: underline dotted;
}

.report-content >>> .text-danger { color: #d63031 !important; }
.report-content >>> .text-success { color: #27ae60 !important; }

/* 响应式调整 */
@media (min-width: 768px) {
  .info-bar {
    border-left: 1px solid rgba(255, 255, 255, 0.4);
    padding-left: 1rem;
  }
}

.detail-body {
  background-color: #fff;
}

/* 暗黑模式适配 */
body.theme-dark .detail-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%);
  border-bottom: 1px solid #333;
}
body.theme-dark .detail-card {
  border-color: #333 !important;
}
body.theme-dark .detail-body {
  background-color: #1a1a1a !important;
}
body.theme-dark .report-content {
  color: #b0b3b8;
}
body.theme-dark .report-content >>> .text-primary {
  color: #58a6ff !important;
}
body.theme-dark .report-content >>> .text-dark {
  color: #f0f6fc !important;
}
body.theme-dark .report-content >>> .stock-tag {
  background-color: #21262d;
}
body.theme-dark .report-content >>> .text-danger {
  color: #ff7b72 !important;
}
body.theme-dark .report-content >>> .text-success {
  color: #3fb950 !important;
}
body.theme-dark .report-content >>> .section-badge {
  opacity: 0.85;
}
body.theme-dark .report-content >>> .theme-badge { background-color: #1f6feb; }
body.theme-dark .report-content >>> .logic-badge { background-color: #238636; }
body.theme-dark .report-content >>> .catalyst-badge { background-color: #da3633; }
body.theme-dark .report-content >>> .stock-badge { background-color: #30363d; }
body.theme-dark .report-content >>> .other-badge { background-color: #21262d; }

.subscription-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

body.theme-dark .subscription-overlay {
  background: rgba(30, 30, 30, 0.7);
}

.overlay-content {
  text-align: center;
  background: var(--el-bg-color, #fff);
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  max-width: 400px;
}

body.theme-dark .overlay-content {
  background: #2d2d2d;
}

.lock-icon {
  color: #e6a23c;
}

.overlay-content h3 {
  margin-bottom: 12px;
  color: var(--el-text-color-primary, #303133);
}

.overlay-content p {
  color: var(--el-text-color-secondary, #909399);
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 1.6;
}

.chart-blurred {
  filter: blur(8px);
  pointer-events: none;
  user-select: none;
}
</style>
